import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import { Icon, Toast } from '@plone/volto/components';
import FormFieldWrapper from '@plone/volto/components/manage/Widgets/FormFieldWrapper';
import { getContent, getVocabulary } from '@plone/volto/actions';

import ContactPickerModal from './ContactPickerModal';

import clearSVG from '@plone/volto/icons/clear.svg';
import selectSVG from '@plone/volto/icons/add.svg';
import mapSVG from '@plone/volto/icons/map.svg';
import emailSVG from '@plone/volto/icons/email.svg';
import phoneSVG from 'volto-proseriestools/icons/phone.svg';
import userSVG from '@plone/volto/icons/user.svg';
import groupSVG from '@plone/volto/icons/group.svg';

const US_COUNTRY = 'United States';

const copyToClipboard = (text, message) => {
  navigator.clipboard?.writeText(text || '');
  toast.success(<Toast success title="Copied" content={message} />);
};

const ContactWidget = (props) => {
  const { id, value, onChange } = props;

  const dispatch = useDispatch();
  const [pickerOpen, setPickerOpen] = useState(false);

  useEffect(() => {
    dispatch(getVocabulary({ vocabNameOrURL: 'contacts' }));
  }, [dispatch]);

  const contactsVocabulary = useSelector(
    (state) => state.vocabularies?.contacts?.items || [],
  );

  const contactTitle =
    contactsVocabulary.find((item) => item.value === value)?.label || value;

  // Fetch the full contact record for the currently selected contact so its
  // postal code can be shown alongside the name. Keyed per contact so
  // switching contact doesn't show a stale postal code while the new one
  // loads.
  const contactSubrequest =
    value && value !== '000000' ? `contact-widget-${value}` : null;

  useEffect(() => {
    if (contactSubrequest) {
      dispatch(getContent(`/contacts/${value}`, null, contactSubrequest));
    }
  }, [dispatch, value, contactSubrequest]);

  const contactDetails = useSelector((state) =>
    contactSubrequest
      ? state.content.subrequests?.[contactSubrequest]?.data
      : undefined,
  );

  const displaySuffix =
    contactDetails?.country === US_COUNTRY
      ? contactDetails?.postal_code
        ? `Zip: ${contactDetails.postal_code}`
        : null
      : contactDetails?.country || null;

  const displayTitle = displaySuffix
    ? `${contactTitle} (${displaySuffix})`
    : contactTitle;

  const hasAddress = !!(
    contactDetails?.street ||
    contactDetails?.city ||
    contactDetails?.state ||
    contactDetails?.postal_code
  );
  const showCopyButton =
    value &&
    value !== '000000' &&
    hasAddress &&
    contactDetails?.country === US_COUNTRY;

  const onCopyAddress = () => {
    const address = `${contactDetails?.street || ''}, ${
      contactDetails?.city || ''
    } ${contactDetails?.state || ''}, ${contactDetails?.postal_code || ''}`;
    copyToClipboard(address, 'Address copied to clipboard.');
  };

  const showCopyEmailButton =
    value && value !== '000000' && !!contactDetails?.email;

  const onCopyEmail = () => {
    copyToClipboard(contactDetails?.email, 'Email copied to clipboard.');
  };

  const showCopyPhoneButton =
    value && value !== '000000' && !!contactDetails?.phone;

  const onCopyPhone = () => {
    const phone = contactDetails?.phone || '';
    // Phone numbers are stored in E.164 format (e.g. "+15056327039"); drop
    // the "+1" country code for US numbers.
    const formatted = phone.startsWith('+1') ? phone.slice(2) : phone;
    copyToClipboard(formatted, 'Phone number copied to clipboard.');
  };

  const showCopyFirstNameButton =
    value && value !== '000000' && !!contactDetails?.first_name;

  const onCopyFirstName = () => {
    copyToClipboard(
      contactDetails?.first_name,
      'First name copied to clipboard.',
    );
  };

  const showCopyLastNameButton =
    value && value !== '000000' && !!contactDetails?.last_name;

  const onCopyLastName = () => {
    copyToClipboard(
      contactDetails?.last_name,
      'Last name copied to clipboard.',
    );
  };

  const onSelectContact = (contactId) => {
    onChange(id, contactId);
    setPickerOpen(false);
    dispatch(getVocabulary({ vocabNameOrURL: 'contacts' }));
  };

  return (
    <FormFieldWrapper {...props} className="text">
      <div className="contact-widget-wrapper">
        <div
          id={`field-${id}`}
          className="contact-widget-value"
          role="button"
          tabIndex={0}
          onClick={() => setPickerOpen(true)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setPickerOpen(true);
            }
          }}
        >
          {displayTitle}
        </div>
        {showCopyFirstNameButton && (
          <button
            type="button"
            className="item ui noborder button"
            onClick={onCopyFirstName}
            aria-label="Copy first name"
          >
            <Icon name={userSVG} size="24px" className="close" />
          </button>
        )}
        {showCopyLastNameButton && (
          <button
            type="button"
            className="item ui noborder button"
            onClick={onCopyLastName}
            aria-label="Copy last name"
          >
            <Icon name={groupSVG} size="24px" className="close" />
          </button>
        )}
        {showCopyButton && (
          <button
            type="button"
            className="item ui noborder button"
            onClick={onCopyAddress}
            aria-label="Copy address"
          >
            <Icon name={mapSVG} size="24px" className="close" />
          </button>
        )}
        {showCopyEmailButton && (
          <button
            type="button"
            className="item ui noborder button"
            onClick={onCopyEmail}
            aria-label="Copy email"
          >
            <Icon name={emailSVG} size="24px" className="close" />
          </button>
        )}
        {showCopyPhoneButton && (
          <button
            type="button"
            className="item ui noborder button"
            onClick={onCopyPhone}
            aria-label="Copy phone"
          >
            <Icon name={phoneSVG} size="24px" className="close" />
          </button>
        )}
        {value && value !== '000000' ? (
          <button
            type="button"
            className="item ui noborder button"
            onClick={() => onChange(id, '000000')}
            aria-label="Clear"
          >
            <Icon name={clearSVG} size="24px" className="close" />
          </button>
        ) : (
          <button
            type="button"
            className="item ui noborder button"
            onClick={() => setPickerOpen(true)}
            aria-label="Select"
          >
            <Icon name={selectSVG} size="24px" className="close" />
          </button>
        )}
      </div>
      <ContactPickerModal
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onSelect={onSelectContact}
        selectedId={value}
      />
    </FormFieldWrapper>
  );
};

export default ContactWidget;

ContactWidget.propTypes = {
  id: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  required: PropTypes.bool,
  error: PropTypes.arrayOf(PropTypes.string),
  value: PropTypes.string,
  focus: PropTypes.bool,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  onClick: PropTypes.func,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
  icon: PropTypes.shape({
    xmlns: PropTypes.string,
    viewBox: PropTypes.string,
    content: PropTypes.string,
  }),
  iconAction: PropTypes.func,
  minLength: PropTypes.number,
  maxLength: PropTypes.number,
  wrapped: PropTypes.bool,
  placeholder: PropTypes.string,
};

ContactWidget.defaultProps = {
  description: null,
  required: false,
  error: [],
  value: null,
  onChange: () => {},
  onBlur: () => {},
  onClick: () => {},
  onEdit: null,
  onDelete: null,
  focus: false,
  icon: null,
  iconAction: null,
  minLength: null,
  maxLength: null,
};
